/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Button } from '@uhg-abyss/web/ui/Button';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { MemberHistoryTableColumns } from '../MemberHistoryTable/MemberHistoryTable';
import { ActionMenuItem } from '@uhg-abyss/web/ui/ActionMenu';
import { TMemberHistoryTableRow } from '@repo/utils';

export type EditMemberHistoryProps = {
  updating: TMemberHistoryTableRow;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onCloseDrawer: () => void;
  onSubmitUpdate: (
    closedLoopStatus: string,
    reasonNotFulfilled: string
  ) => void;
};

export const EditMemberHistory = ({
  updating,
  isOpen,
  setIsOpen,
  onCloseDrawer,
  onSubmitUpdate,
}: EditMemberHistoryProps) => {
  const [closedLoopStatus, setClosedLoopStatus] = useState<string>('');
  const [reasonNotFulfilled, setReasonNotFulfilled] = useState<string>('');

  // Same columns as the MemberHistoryTable, minus the closed loop ones since
  // that's what the user is doing in this form.
  const displayCols = MemberHistoryTableColumns.filter((col) =>
    col.id !== 'closedLoopStatus' && col.id !== 'reasonNotFulfilled'
  );

  const onClickClosedLoopStatus = (status: string) => {
    setClosedLoopStatus(status);
    setReasonNotFulfilled('');
  };

  const menuItems: ActionMenuItem[] =
    updating.type.toLocaleLowerCase() === 'referral'
      ? [
          // Referral options
          { title: ' ', onClick: () => onClickClosedLoopStatus('') },
          {
            title: 'Needs met',
            onClick: () => onClickClosedLoopStatus('Needs met'),
          },
          {
            title: 'Needs unmet',
            onClick: () => onClickClosedLoopStatus('Needs unmet'),
          },
        ]
      : [
          // Screener options
          { title: ' ', onClick: () => onClickClosedLoopStatus('') },
          { title: 'Closed', onClick: () => onClickClosedLoopStatus('Closed') },
        ];

  return (
    <React.Fragment>
      <Drawer
        header="Edit Member History"
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          onCloseDrawer();
        }}
        size="800px"
        footer={
          <div>
            <Layout.Group alignLayout="right">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  width: '100%',
                  paddingBottom: '4px',
                }}
              >
                <div style={{ marginRight: '1em' }}>
                  <Text fontSize="sm">
                    (Once a referral status is updated it may take up to 24
                    hours to be reflected within the Member History grid.)
                  </Text>
                </div>
                <Button
                  isDisabled={
                    closedLoopStatus === '' ||
                    (closedLoopStatus === 'Needs unmet' &&
                      reasonNotFulfilled === '') ||
                    (closedLoopStatus === 'Closed' && reasonNotFulfilled === '')
                  }
                  href="#"
                  data-cy="update-button"
                  onClick={() => {
                    onSubmitUpdate(closedLoopStatus, reasonNotFulfilled);
                  }}
                >
                  Update
                </Button>
              </div>
            </Layout.Group>
          </div>
        }
      >
        <div>
          <div className="contents">
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px', border: '1px solid #CBCCCD', borderRadius: '8px', overflow: 'hidden' }}>
              <thead>
                <tr>
                  {displayCols.map((col, i) => (
                    <th key={col.id} style={{ textAlign: 'left', padding: '6px 8px', backgroundColor: '#F3F3F3', borderBottom: '1px solid #CBCCCD', fontWeight: 'bold', color: '#000000', ...(i < displayCols.length - 1 && { borderRight: '1px solid #CBCCCD' }) }}>
                      {String(col.header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {displayCols.map((col, i) => (
                    <td key={col.id} style={{ padding: '6px 8px', ...(i < displayCols.length - 1 && { borderRight: '1px solid #CBCCCD' }) }}>
                      {col.id === 'date'
                        ? dayjs(updating.date).format('MM/DD/YYYY')
                        : String((updating as any)[col.accessorKey as string] ?? '')}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div data-cy="closed-loop-status">
            <label
              style={{
                paddingBottom: '10px',
                paddingTop: '10px',
                fontWeight: 'bold',
                color: '#000000',
                marginTop: '16px',
                display: 'block',
              }}
            >
              Closed Loop Status
            </label>
            <div style={{ width: '100%' }}>
            <DropdownMenu
              outline
              data-cy="status-options"
              label={closedLoopStatus}
              menuItems={menuItems}
              css={{
                'abyss-dropdown-menu-trigger-root': {
                  width: '25%',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                'abyss-icon': {
                  position: 'relative',
                  right: 'auto !important',
                  margin: '0 !important',
                  marginLeft: 'auto !important',
                },
                'abyss-dropdown-menu-content': {
                  backgroundColor: 'white',
                  width: '100%',
                  height: '96px',
                },
                'abyss-dropdown-menu-item-separator': {
                  backgroundColor: 'black',
                  zIndex: '999',
                },
              }}
            />
            </div>
          </div>
          <div data-cy="reason-not-fulfilled" style={{ marginTop: '8px' }}>
            {/* Screener */}
            {closedLoopStatus === 'Closed' && (
              <ScreenerClosedReasonCheckboxGroup
                selection={reasonNotFulfilled}
                onSelectionChange={setReasonNotFulfilled}
              />
            )}

            {/* Referral */}
            {closedLoopStatus === 'Needs unmet' && (
              <ReferralNotFulfilledReasonCheckboxGroup
                selection={reasonNotFulfilled}
                onSelectionChange={setReasonNotFulfilled}
              />
            )}
          </div>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

type CheckboxGroupProps = {
  selection: string;
  onSelectionChange: (selection: string) => void;
};

function ScreenerClosedReasonCheckboxGroup({
  selection,
  onSelectionChange,
}: CheckboxGroupProps) {
  return (
    <RadioGroup
      label="Reason not fulfilled"
      css={{ 'abyss-radio-group-label': { color: '#000000', fontWeight: 'bold' } }}
      onChange={(e) => {
        onSelectionChange(e.target.value);
      }}
      value={selection}
    >
      <RadioGroup.Radio
        label="Not interested - Do not contact"
        value="Not interested - Do not contact"
      />
      <RadioGroup.Radio
        label="Not eligible (not a UHC member)"
        value="Not eligible (not a UHC member)"
      />
      <RadioGroup.Radio
        label="Not interested (in assistance)"
        value="Not interested (in assistance)"
      />
      <RadioGroup.Radio label="Member deceased" value="Member deceased" />
      <RadioGroup.Radio
        label="Not eligible for this outreach - Institutionalized"
        value="Not eligible for this outreach - Institutionalized"
      />
      <RadioGroup.Radio
        label="Problem resolved via other resource"
        value="Problem resolved via other resource"
      />
    </RadioGroup>
  );
}

function ReferralNotFulfilledReasonCheckboxGroup({
  selection,
  onSelectionChange,
}: CheckboxGroupProps) {
  return (
    <RadioGroup
      label="Reason not fulfilled"
      css={{ 'abyss-radio-group-label': { color: '#000000', fontWeight: 'bold' } }}
      onChange={(e) => {
        onSelectionChange(e.target.value);
      }}
      value={selection}
    >
      <Grid>
        <Grid.Col span="50%">
          <RadioGroup.Radio
            label="Action taken, no benefit received"
            value="Action taken, no benefit received"
          />
          <RadioGroup.Radio
            label="Community program did not have the resources needed"
            value="Community program did not have the resources needed"
          />
          <RadioGroup.Radio
            label="Have not contacted program yet"
            value="Have not contacted program yet"
          />
          <RadioGroup.Radio
            label="Member not eligible for program"
            value="Member not eligible for program"
          />
          <RadioGroup.Radio
            label="Member refused assistance"
            value="Member refused assistance"
          />
          <RadioGroup.Radio
            label="Unable to contact"
            value="Unable to contact"
          />
        </Grid.Col>
        <Grid.Col span="50%">
          <RadioGroup.Radio
            label="No longer need assistance"
            value="No longer need assistance"
          />
          <RadioGroup.Radio
            label="No reason provided"
            value="No reason provided"
          />
          <RadioGroup.Radio
            label="No resources available"
            value="No resources available"
          />
          <RadioGroup.Radio label="Other reason" value="Other reason" />
          <RadioGroup.Radio
            label="Referred elsewhere"
            value="Referred elsewhere"
          />
          <RadioGroup.Radio
            label="Unable to access assistance"
            value="Unable to access assistance"
          />
        </Grid.Col>
      </Grid>
    </RadioGroup>
  );
}
